export type CountryOption = {
  id: string
  text: string
  englishName: string
}

// ISO 3166-1 alpha-2, 249 officially assigned entries.
// Sources:
// - https://www.iso.org/iso-3166-country-codes.html
// - https://unstats.un.org/unsd/methodology/m49/overview/
export const ISO_3166_1_ALPHA2_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
] as const

// Nominatim commonly returns XK for Kosovo. XK is not an officially assigned
// ISO 3166-1 code, so it is deliberately kept outside the canonical list.
const GEOCODER_COMPATIBILITY_CODES = ['XK'] as const

const DISPLAY_LOCALE_ALIASES: Record<string, string> = {
  cn: 'zh-CN',
  zh: 'zh-TW',
}

type RegionDisplayNames = {
  of: (code: string) => string | undefined
}

type RegionDisplayNamesConstructor = new (
  locales: string | string[],
  options: { type: 'region'; fallback: 'code' },
) => RegionDisplayNames

const getDisplayNames = (locale: string): RegionDisplayNames | null => {
  const Constructor = (
    Intl as unknown as { DisplayNames?: RegionDisplayNamesConstructor }
  ).DisplayNames

  if (!Constructor) return null

  try {
    const normalizedLocale =
      DISPLAY_LOCALE_ALIASES[locale.toLowerCase()] || locale.replace('_', '-')
    return new Constructor([normalizedLocale, 'en'], {
      type: 'region',
      fallback: 'code',
    })
  } catch {
    return new Constructor('en', { type: 'region', fallback: 'code' })
  }
}

export const getCountryOptions = (locale = 'en'): CountryOption[] => {
  const localizedNames = getDisplayNames(locale)
  const englishNames = getDisplayNames('en')
  const codes = [
    ...ISO_3166_1_ALPHA2_CODES,
    ...GEOCODER_COMPATIBILITY_CODES,
  ]

  return codes
    .map((id) => {
      const fallbackName = id === 'XK' ? 'Kosovo' : id
      return {
        id,
        text: localizedNames?.of(id) || fallbackName,
        englishName: englishNames?.of(id) || fallbackName,
      }
    })
    .sort((left, right) =>
      left.text.localeCompare(right.text, locale, { sensitivity: 'base' }),
    )
}
